import { useMemo } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { Home, LayoutDashboard, Ticket, MessagesSquare, Tag, Code2, LogOut, LogIn, Mail, Cpu, ClipboardCheck, Users, Contact, Receipt, BookOpenCheck, MessageCircle, ArrowRight, ShieldCheck } from "lucide-react";
import { BRAND } from "@/lib/api";
import { LifetimeBadge } from "@/components/Badges";

const NAV = [
  { to: "/", label: "Home", icon: Home, id: "sidebar-home-link", end: true },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "sidebar-dashboard-link", auth: true },
  { to: "/tickets", label: "Tickets", icon: Ticket, id: "sidebar-tickets-link", auth: true },
  { to: "/approvals", label: "Approvals", icon: ClipboardCheck, id: "sidebar-approvals-link", admin: true, badge: "approvals" },
  { to: "/staff", label: "Engineers", icon: Users, id: "sidebar-staff-link", admin: true, owner: true },
  { to: "/audit", label: "Audit log", icon: ShieldCheck, id: "sidebar-audit-link", admin: true, owner: true },
  { to: "/customers", label: "Customers", icon: Contact, id: "sidebar-customers-link", admin: true },
  { to: "/machines", label: "My machines", icon: Cpu, id: "sidebar-machines-link", auth: true },
  { to: "/community", label: "Community", icon: MessagesSquare, id: "sidebar-community-link" },
  { to: "/fixes", label: "Known fixes", icon: BookOpenCheck, id: "sidebar-fixes-link" },
  { to: "/for-sale", label: "For sale", icon: Tag, id: "sidebar-forsale-link" },
  { to: "/messages", label: "Messages", icon: Mail, id: "sidebar-messages-link", auth: true, badge: "messages" },
  { to: "/payments", label: "Payments", icon: Receipt, id: "sidebar-payments-link", auth: true },
  { to: "/widget", label: "Website widget", icon: Code2, id: "sidebar-widget-link", auth: true, admin: true, owner: true },
];

const navClass = ({ isActive }) =>
  `flex items-center gap-3 rounded-md border-l-2 px-3 py-2 text-sm transition-colors ${
    isActive ? "border-rose-500 bg-slate-800/70 text-white" : "border-transparent text-slate-400 hover:bg-slate-800/40 hover:text-slate-100"
  }`;

export const SideNav = ({ user, badges, onNavigate }) => {
  const items = useMemo(() => NAV.filter((n) => (!n.auth || user) && (!n.admin || user?.role === "admin") && (!n.owner || user?.superadmin)), [user]);
  return (
    <nav className="space-y-1">
      {items.map(({ to, label, icon: Icon, id, end, badge }) => (
        <NavLink key={to} to={to} end={end} onClick={onNavigate} data-testid={id} className={navClass}>
          <Icon className="h-4 w-4" /> {label}
          {badge && badges[badge] > 0 && <span className="ml-auto rounded-full bg-rose-600 px-1.5 py-0.5 text-[10px] font-medium leading-none text-white" data-testid={`sidebar-${badge}-badge`}>{badges[badge]}</span>}
        </NavLink>
      ))}
    </nav>
  );
};

const OpenTicketsLink = ({ openCount, onNavigate }) => (
  <Link to="/tickets" onClick={onNavigate} className="mt-1 flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs text-slate-400 transition-colors hover:bg-slate-800 hover:text-white" data-testid="sidebar-open-tickets">
    <span className="rounded-sm bg-rose-600 px-1.5 py-0.5 font-mono text-[10px] text-white" data-testid="sidebar-open-tickets-count">{openCount}</span>
    {openCount === 1 ? "open ticket" : "open tickets"} <ArrowRight className="h-3 w-3" />
  </Link>
);

export const HelpBlock = ({ user, openCount, onNavigate }) => {
  const { pathname } = useLocation();
  const here = pathname.startsWith("/tickets/new");
  return (
    <div className="border-t border-slate-800 pt-4" data-testid="sidebar-help-block">
      <p className="mb-2 px-3 font-mono text-[11px] uppercase tracking-widest text-slate-500">Need help?</p>
      {here ? (
        <span className="btn-primary w-full cursor-default opacity-50" aria-disabled="true" data-testid="sidebar-submit-ticket-here">You're here</span>
      ) : (
        <Link to={user ? "/tickets/new" : "/auth?next=/tickets/new"} onClick={onNavigate} className="btn-primary w-full" data-testid="sidebar-submit-ticket"><Ticket className="h-4 w-4" /> Submit a ticket</Link>
      )}
      <p className="mt-1.5 px-3 text-center text-[11px] text-slate-500" data-testid="sidebar-ticket-note">Free, or £30 for a priority response</p>
      <a href={BRAND.whatsappLink} target="_blank" rel="noreferrer" className="mt-2 flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm text-slate-300 transition-colors hover:bg-slate-800 hover:text-white" data-testid="sidebar-whatsapp">
        <MessageCircle className="h-4 w-4 text-emerald-400" /> WhatsApp us
      </a>
      {user?.role === "customer" && openCount > 0 && <OpenTicketsLink openCount={openCount} onNavigate={onNavigate} />}
    </div>
  );
};

export const UserBox = ({ user, logout }) => {
  const navigate = useNavigate();
  if (!user)
    return (
      <Link to="/auth" data-testid="sidebar-signin-link" className="flex items-center gap-2 rounded-md bg-rose-600 px-3 py-2 text-sm font-medium text-white hover:bg-rose-500">
        <LogIn className="h-4 w-4" /> Sign in
      </Link>
    );
  return (
    <div className="rounded-md border border-slate-800 bg-slate-950/60 p-3">
      <p className="truncate text-sm font-medium text-white" data-testid="current-user-name">{user.name}</p>
      <p className="truncate font-mono text-[11px] uppercase tracking-wider text-slate-500" data-testid="current-user-role">{user.role}</p>
      {user.lifetime_support && <LifetimeBadge testId="current-user-lifetime-badge" className="mt-2" />}
      <button data-testid="logout-button" onClick={async () => { await logout(); navigate("/"); }} className="mt-2 flex items-center gap-2 text-xs text-slate-400 hover:text-rose-400">
        <LogOut className="h-3.5 w-3.5" /> Sign out
      </button>
    </div>
  );
};
